import { useGetPartnerList, usePartnerInfo } from "@/api/services/main/partner";
import { TPartner } from "@/api/entities/partner.entity";
import { TGetPartnerDto } from "@/api/services/main/partner/dtos/get-partner.dto";
import { IconNavigation } from "@/assets/icons";
import { Checkbox } from "@/components/elements/Checkbox";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { UseVisibleReturn, useVisible } from "@/hooks/useVisible";
import { divider, black } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { Stack, Box, Typography, Grid, CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useRef } from "react";
import { convertNumberToZipCode } from "@/utils/input";
import { findLabelByValue } from "@/utils/object";
import { useEnum } from "@/api/services/global/enum";
import { JAPAN_NATIONALITY_ID } from "@/types/enum";

type TalentFormPartnerInfoProps = {
  formPartnerModal: UseVisibleReturn;
  onEditPartner: (partnerData: TPartner | TGetPartnerDto) => void;
  onClearPartner?: () => void;
  onRefetchPartnerInfo?: (refetchFn: () => void) => void;
  talentId?: string;
};

export const TalentFormPartnerInfo: FCC<TalentFormPartnerInfoProps> = ({
  formPartnerModal,
  onEditPartner,
  onClearPartner,
  onRefetchPartnerInfo,
  talentId,
}) => {
  const methods = useFormContext<TTalentForm>();
  const { control, getValues } = methods;
  const { partnerOptions } = useGetPartnerList();
  const { data: contactMethodOptions } = useEnum("EPartnerContactMethod");

  const partnerId = useWatch({ name: "partnerId" });

  const { data: partnerInfo, isLoading: isPartnerLoading, refetch: refetchPartnerInfo } = usePartnerInfo(partnerId);

  const defaultPartner = getValues("partner");

  const partner = partnerId ? partnerInfo?.data : defaultPartner;

  const isLoadingNewPartner = isPartnerLoading && partnerId;

  const isNotJapan = Number(partner?.nationalityId) !== JAPAN_NATIONALITY_ID;

  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  const menuVisible = useVisible(false);

  // Pass refetch function to parent component
  useEffect(() => {
    if (onRefetchPartnerInfo && refetchPartnerInfo) {
      onRefetchPartnerInfo(refetchPartnerInfo);
    }
  }, [onRefetchPartnerInfo, refetchPartnerInfo]);

  const handleNewPartner = () => {
    if (talentId) {
      menuVisible.close();
    }
    if (onClearPartner) {
      onClearPartner();
    }
    formPartnerModal.open();
  };

  const handleEditPartner = () => {
    menuVisible.close();
    if (partner) {
      onEditPartner(partner);
    }
    formPartnerModal.open();
  };

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="head20Bold" color={black[30]}>
            パートナー情報
          </Typography>
        </Box>

        <IconButton ref={menuAnchorRef} onClick={menuVisible.open} size="small">
          <IconNavigation />
        </IconButton>
        <Menu
          anchorEl={menuAnchorRef.current}
          open={menuVisible.visible}
          onClose={menuVisible.close}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleNewPartner}>パートナー新規登録</MenuItem>
          {partnerId && <MenuItem onClick={handleEditPartner}>パートナー情報を編集</MenuItem>}
        </Menu>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FormField control={control} name="partnerId">
              <Select options={partnerOptions} />
            </FormField>
          </Grid>

          {partnerId && partner && (
            <>
              {isLoadingNewPartner && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "20px",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                </Grid>
              )}

              {partnerId && partner?.sendingOrganization && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderRadius: rounded.md,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1px solid ${black[95]}`,
                      color: black[30],
                      ...typography.cap12,
                      lineHeight: 1,
                      padding: "4px 10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    送り出し機関
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} mt={1}>
                <FormField name="partnerNationality" required>
                  <Input
                    readonly
                    value={isLoadingNewPartner ? "" : partnerId ? partner?.nationality?.nationalityName : ""}
                    disabled={isLoadingNewPartner}
                  />
                </FormField>
              </Grid>

              {!isNotJapan && (
                <Grid item xs={12} mt={1}>
                  <FormField name="partnerZipCode">
                    <Input
                      readonly
                      value={isLoadingNewPartner ? "" : partnerId ? (partner?.zipCode ? convertNumberToZipCode(partner?.zipCode) : "") : ""}
                      disabled={isLoadingNewPartner}
                    />
                  </FormField>
                </Grid>
              )}

              {!isNotJapan && (
                <Grid item xs={6} mt={1}>
                  <FormField name="partnerPrefecture">
                    <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.prefecture : ""} disabled={isLoadingNewPartner} />
                  </FormField>
                </Grid>
              )}

              {!isNotJapan && (
                <Grid item xs={6} mt={1}>
                  <FormField name="partnerCity">
                    <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.city : ""} disabled={isLoadingNewPartner} />
                  </FormField>
                </Grid>
              )}

              {!isNotJapan && (
                <Grid item xs={12} mt={1}>
                  <FormField name="partnerAddress">
                    <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.address : ""} disabled={isLoadingNewPartner} />
                  </FormField>
                </Grid>
              )}

              {isNotJapan && (
                <Grid item xs={12} mt={1}>
                  <FormField name="partnerLocation">
                    <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.location : ""} disabled={isLoadingNewPartner} />
                  </FormField>
                </Grid>
              )}

              <Grid item xs={12} mt={1}>
                <FormField name="partnerContactPerson">
                  <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.contactPerson : ""} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerContactMethod">
                  <Input
                    readonly
                    value={isLoadingNewPartner ? "" : partnerId ? findLabelByValue(contactMethodOptions, partner?.contactMethod) : ""}
                    disabled={isLoadingNewPartner}
                  />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerPhoneNumber">
                  <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.phoneNumber : ""} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerEmail">
                  <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.email : ""} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerOtherContacts">
                  <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.otherContacts : ""} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerLaborBureauStatus">
                  <Checkbox readOnly={true} disabled={true} checked={partnerId ? !!partner?.laborBureauStatus : false} label="済" />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerContractInfo">
                  <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.contractInfo : ""} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerBillingInfo">
                  <Input readonly value={isLoadingNewPartner ? "" : partnerId ? partner?.billingInfo : ""} disabled={isLoadingNewPartner} />
                </FormField>
              </Grid>

              <Grid item xs={12} mt={1}>
                <FormField name="partnerMemo">
                  <Textarea
                    readonly
                    value={isLoadingNewPartner ? "" : partnerId && partner?.memo?.length ? partner.memo : ""}
                    disabled={isLoadingNewPartner}
                  />
                </FormField>
              </Grid>
            </>
          )}
        </Grid>
      </Stack>
    </InnerBox>
  );
};
